import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './GuestRSVP.css';

const EventImage = ({ bannerUrl, altText }) => (
  <div className="event-image-container">
    <img
      src={bannerUrl || '/logo.png'}
      alt={altText || 'Event Banner'}
      className="event-image"
      loading="lazy"
      sizes="(max-width: 480px) 100vw, 480px"
    />
  </div>
);

export default function RSVPPage() {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [isCodeValidated, setIsCodeValidated] = useState(false);
  const [step, setStep] = useState(1);
  const [isAttending, setIsAttending] = useState(null);
  const [numGuests, setNumGuests] = useState(0);
  const [guestNames, setGuestNames] = useState([]);
  const [mealSelections, setMealSelections] = useState([]);
  const [customAnswers, setCustomAnswers] = useState([]);
  const [optionalMessage, setOptionalMessage] = useState('');
  const [rsvpName, setRsvpName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    async function loadEvent() {
      try {
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (!eventDoc.exists()) throw new Error('Event not found');
        const data = eventDoc.data();
        setEventData({
          ...data,
          fields: Array.isArray(data.fields) ? data.fields : [{ value: data.fields || '' }],
          title: data.title || (data.coupleNames ? `${data.coupleNames}'s Wedding` : 'Our Wedding'),
        });
        setMealSelections(['']);
        setCustomAnswers(new Array((data.customQuestions?.length || 0)).fill(''));
        if (!data.eventCode) setIsCodeValidated(true);
        setDataReady(true);
      } catch (err) {
        console.error('Error loading event:', err.message);
        alert('Failed to load event: ' + err.message);
        setDataReady(true);
      } finally {
        setLoading(false);
      }
    }
    loadEvent();
  }, [eventId]);

  useEffect(() => {
    if (dataReady && eventData) {
      document.title = eventData.title
        ? `${eventData.title} RSVP`
        : eventData.coupleNames
        ? `${eventData.coupleNames} RSVP`
        : 'Wedding RSVP';
    }
  }, [dataReady, eventData]);

  useEffect(() => {
    if (!dataReady || !eventData || !isAttending) return;
    setGuestNames((prev) => {
      const updated = [...prev];
      while (updated.length < numGuests) updated.push('');
      while (updated.length > numGuests) updated.pop();
      return updated;
    });
    setMealSelections((prev) => {
      const updated = [...prev];
      while (updated.length < numGuests) updated.push('');
      while (updated.length > numGuests) updated.pop();
      return updated;
    });
  }, [numGuests, eventData, dataReady, isAttending]);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code === eventData.eventCode) {
      setIsCodeValidated(true);
      setCodeError('');
    } else {
      setCodeError('Invalid event code.');
    }
  };

  const handleMealChange = (index, value) => {
    const updated = [...mealSelections];
    updated[index] = value;
    setMealSelections(updated);
  };

  const handleGuestNameChange = (index, value) => {
    const updated = [...guestNames];
    updated[index] = value;
    setGuestNames(updated);
  };

  const handleCustomAnswerChange = (index, value) => {
    const updated = [...customAnswers];
    updated[index] = value;
    setCustomAnswers(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && step < (isAttending ? 3 : 3)) {
      e.preventDefault();
      nextStep();
    }
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 1 && isAttending === null) {
      newErrors.attending = 'Please select whether you will attend';
    }
    if (step === 2 && isAttending && (numGuests < 1 || numGuests > 9)) {
      newErrors.numGuests = 'Please select a valid number of guests (1-9)';
    }
    if (step === 2 && !isAttending && !rsvpName.trim()) {
      newErrors.rsvpName = 'Please enter your name';
    }
    if (step === 3 && isAttending) {
      guestNames.forEach((name, i) => {
        if (!name.trim()) newErrors[`guestName${i}`] = `Please enter a name for guest ${i + 1}`;
      });
      if (eventData.mealOptions?.length) {
        mealSelections.forEach((meal, i) => {
          if (!meal) newErrors[`meal${i}`] = `Please select a meal for guest ${i + 1}`;
        });
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      if (step === 1 && isAttending) {
        setNumGuests(1); // Set default to 1 when attending
      }
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const rsvpDocRef = doc(collection(db, 'events', eventId, 'rsvps'));
      const rsvpData = {
        guestName: isAttending ? guestNames[0]?.trim() || '' : rsvpName.trim(),
        numGuests: isAttending ? numGuests : 0,
        guestNames: isAttending ? guestNames.map((name) => name.trim()) : [],
        mealSelections: isAttending ? mealSelections : [],
        customAnswers: isAttending ? customAnswers : [],
        optionalMessage: isAttending ? '' : optionalMessage.trim(),
        isAttending,
        eventCode: eventData.eventCode || null,
        eventId,
        createdAt: serverTimestamp(),
      };
      await setDoc(rsvpDocRef, rsvpData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting RSVP:', err.message);
      alert('Failed to submit RSVP: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const defaultMealOptions = ['Chicken', 'Beef', 'Vegetarian'];

  if (loading || !dataReady) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading event details...</p>
            <div className="skeleton-heading"></div>
            <div className="skeleton-subheading"></div>
            <div className="skeleton-form"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 className="rsvp-heading">Event Not Found</h1>
          <p className="error-message">The requested event could not be found.</p>
        </div>
      </div>
    );
  }

  if (!isCodeValidated) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 className="rsvp-heading">Enter Event Code</h1>
          <form onSubmit={handleCodeSubmit} className="rsvp-form">
            <div className="form-step">
              <label htmlFor="code" className="rsvp-label">Event Code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter event code"
                required
                className={`rsvp-input ${codeError ? 'error' : ''}`}
                autoFocus
                aria-describedby="code-error"
              />
              {codeError && <p id="code-error" className="error-message">{codeError}</p>}
              <div className="step-buttons">
                <button type="submit" className="rsvp-button">Submit Code</button>
              </div>
            </div>
          </form>
          <img
            src="/logo.png"
            alt="Wedding Logo"
            className="rsvp-logo"
            loading="lazy"
            sizes="(max-width: 360px) 80px, 100px"
          />
          <footer className="rsvp-footer">
            <p className="footer-text">
              Created by{' '}
              <a href="https://get-gs.com" target="_blank" rel="noopener noreferrer" className="footer-link">GS</a>
            </p>
          </footer>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 className="rsvp-heading">Thank You{isAttending && guestNames[0] ? `, ${guestNames[0]}!` : rsvpName ? `, ${rsvpName}!` : '!'}</h1>
          <p className="rsvp-subline">Your RSVP has been received.</p>
          <p className="rsvp-subline">
            {isAttending
              ? 'We look forward to celebrating with you!'
              : 'We’re sorry you can’t join us, but thank you for letting us know!'}
          </p>
          <img
            src="/logo.png"
            alt="Wedding Logo"
            className="rsvp-logo"
            loading="lazy"
            sizes="(max-width: 360px) 80px, 100px"
          />
          <footer className="rsvp-footer">
            <p className="footer-text">
              Created by{' '}
              <a href="https://get-gs.com" target="_blank" rel="noopener noreferrer" className="footer-link">GS</a>
            </p>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-page">
      <div className="rsvp-card">
        <EventImage bannerUrl={eventData.bannerUrl} altText={eventData.title || 'Wedding Banner'} />
        <h1 className="rsvp-heading">
          {eventData.title || (eventData.coupleNames ? eventData.coupleNames : 'Our Wedding')}
        </h1>
        <div className="event-details" aria-label="Event details">
          <div className="event-content">
            {eventData.fields[0]?.value || 'No event details provided.'}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="rsvp-form">
          {step === 1 && (
            <div className="form-step">
              <label htmlFor="attending" className="rsvp-label">Will you be attending?</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    id="attending-yes"
                    type="radio"
                    name="isAttending"
                    value="yes"
                    checked={isAttending === true}
                    onChange={() => setIsAttending(true)}
                    required
                  />
                  Yes
                </label>
                <label className="radio-label">
                  <input
                    id="attending-no"
                    type="radio"
                    name="isAttending"
                    value="no"
                    checked={isAttending === false}
                    onChange={() => setIsAttending(false)}
                    required
                  />
                  No
                </label>
              </div>
              {errors.attending && <p id="attending-error" className="error-message">{errors.attending}</p>}
              <div className="step-buttons">
                <button type="button" className="rsvp-button" onClick={nextStep}>Next</button>
              </div>
            </div>
          )}
          {step === 2 && isAttending && (
            <div className="form-step">
              <label htmlFor="numGuests" className="rsvp-label">Number of Guests (including you)</label>
              <select
                id="numGuests"
                value={numGuests}
                onChange={(e) => setNumGuests(parseInt(e.target.value))}
                required
                className={`rsvp-select ${errors.numGuests ? 'error' : ''}`}
                autoFocus
                aria-describedby="numGuests-error"
              >
                {Array.from({ length: 9 }, (_, i) => i + 1).map((num) => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? 'Guest' : 'Guests'}
                  </option>
                ))}
              </select>
              {errors.numGuests && <p id="numGuests-error" className="error-message">{errors.numGuests}</p>}
              <div className="step-buttons">
                <button type="button" className="rsvp-button back" onClick={prevStep}>Back</button>
                <button type="button" className="rsvp-button" onClick={nextStep}>Next</button>
              </div>
            </div>
          )}
          {step === 2 && !isAttending && (
            <div className="form-step">
              <label htmlFor="rsvpName" className="rsvp-label">Your Name</label>
              <input
                id="rsvpName"
                type="text"
                value={rsvpName}
                onChange={(e) => setRsvpName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Your Full Name"
                required
                className={`rsvp-input ${errors.rsvpName ? 'error' : ''}`}
                autoFocus
                aria-describedby="rsvpName-error"
              />
              {errors.rsvpName && <p id="rsvpName-error" className="error-message">{errors.rsvpName}</p>}
              <div className="step-buttons">
                <button type="button" className="rsvp-button back" onClick={prevStep}>Back</button>
                <button type="button" className="rsvp-button" onClick={nextStep}>Next</button>
              </div>
            </div>
          )}
          {step === 3 && isAttending && (
            <div className="form-step">
              {Array.from({ length: numGuests }).map((_, i) => (
                <div key={i} className="guest-meal-selection">
                  <label htmlFor={`guestName${i}`} className="rsvp-label">
                    {i === 0 ? 'Your Name' : `Guest #${i} Name`}
                  </label>
                  <input
                    id={`guestName${i}`}
                    type="text"
                    value={guestNames[i] || ''}
                    onChange={(e) => handleGuestNameChange(i, e.target.value)}
                    placeholder={i === 0 ? 'Your Full Name' : `Guest ${i} Full Name`}
                    required
                    className={`rsvp-input ${errors[`guestName${i}`] ? 'error' : ''}`}
                    aria-describedby={`guestName${i}-error`}
                    autoFocus={i === 0}
                  />
                  {errors[`guestName${i}`] && <p id={`guestName${i}-error`} className="error-message">{errors[`guestName${i}`]}</p>}
                  {eventData.mealOptions?.length > 0 && (
                    <>
                      <label htmlFor={`meal${i}`} className="rsvp-label">
                        {i === 0 ? 'Your Meal' : `Guest #${i} Meal`}
                      </label>
                      <select
                        id={`meal${i}`}
                        value={mealSelections[i] || ''}
                        onChange={(e) => handleMealChange(i, e.target.value)}
                        required
                        className={`rsvp-select ${errors[`meal${i}`] ? 'error' : ''}`}
                        aria-describedby={`meal${i}-error`}
                      >
                        <option value="">Select a meal</option>
                        {(eventData.mealOptions || defaultMealOptions).map((meal, idx) => (
                          <option key={idx} value={meal}>{meal}</option>
                        ))}
                      </select>
                      {errors[`meal${i}`] && <p id={`meal${i}-error`} className="error-message">{errors[`meal${i}`]}</p>}
                    </>
                  )}
                </div>
              ))}
              {(eventData.customQuestions || []).map((question, i) => (
                <div key={i} className="guest-meal-selection">
                  <label htmlFor={`question${i}`} className="rsvp-label">{question || `Question ${i + 1}`}</label>
                  <input
                    id={`question${i}`}
                    type="text"
                    value={customAnswers[i] || ''}
                    onChange={(e) => handleCustomAnswerChange(i, e.target.value)}
                    placeholder="Your answer"
                    className="rsvp-input"
                  />
                </div>
              ))}
              <div className="step-buttons">
                <button type="button" className="rsvp-button back" onClick={prevStep}>Back</button>
                <button type="submit" disabled={submitting} className="rsvp-button submit">
                  {submitting ? 'Submitting...' : 'Submit RSVP'}
                </button>
              </div>
            </div>
          )}
          {step === 3 && !isAttending && (
            <div className="form-step">
              <label htmlFor="optionalMessage" className="rsvp-label">Message (Optional)</label>
              <textarea
                id="optionalMessage"
                value={optionalMessage}
                onChange={(e) => setOptionalMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Share a message with the couple (optional)"
                className="rsvp-textarea"
                rows="4"
                aria-describedby="optionalMessage-hint"
              />
              <small id="optionalMessage-hint" className="form-hint">
                Feel free to share a message with the couple.
              </small>
              <div className="step-buttons">
                <button type="button" className="rsvp-button back" onClick={prevStep}>Back</button>
                <button type="submit" disabled={submitting} className="rsvp-button submit">
                  {submitting ? 'Submitting...' : 'Submit RSVP'}
                </button>
              </div>
            </div>
          )}
        </form>
        <img
          src="/logo.png"
          alt="Wedding Logo"
          className="rsvp-logo"
          loading="lazy"
          sizes="(max-width: 360px) 80px, 100px"
        />
        <footer className="rsvp-footer">
          <p className="footer-text">
            Created by{' '}
            <a href="https://get-gs.com" target="_blank" rel="noopener noreferrer" className="footer-link">GS</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
